"use client";

import Image from "next/image";
import { Typography } from "../ui/typography";
import { cn } from "@/lib/utils";
import ArrowButton from "../shared/ArrowButton";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselDots,
  useCarousel,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { useMemo, useCallback, useRef } from "react";
import { Skeleton } from "../ui/skeleton";

interface Category {
  id: number;
  title: string;
  icon: string;
}

// Category item component - reusable
function CategoryItem({ category }: { category: Category }) {
  return (
    <div className="flex flex-col items-center gap-3 cursor-pointer">
      <div className="relative size-12 md:size-16">
        <Image
          src={category.icon}
          alt={category.title}
          fill
          className="object-contain"
        />
      </div>
      <Typography variant="captionRegular">{category.title}</Typography>
    </div>
  );
}

// Navigation buttons component that uses carousel context
function CategoryNavigationPrev() {
  const { scrollPrev, canScrollPrev } = useCarousel();

  return (
    <ArrowButton
      className="hidden md:!flex -scale-x-100"
      onClick={scrollPrev}
      disabled={!canScrollPrev}
    />
  );
}
function CategoryNavigationNext() {
  const { scrollNext, canScrollNext } = useCarousel();

  return (
    <ArrowButton
      className="hidden md:!flex"
      onClick={scrollNext}
      disabled={!canScrollNext}
    />
  );
}

// Skeleton loading state
export function CategorySectionSkeleton() {
  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <Skeleton className="h-5 w-40 mx-auto" />
      <div className="flex items-center gap-3">
        <Skeleton className="hidden md:block size-[34px] rounded-sm" />
        <div className="flex gap-4 overflow-hidden flex-1 pr-5 md:pr-0">
          {Array.from({ length: 10 }).map((_, index) => (
            <div
              key={index}
              className="flex flex-col items-center gap-3 shrink-0"
            >
              <Skeleton className="size-12 md:size-16 rounded-full" />
              <Skeleton className="h-3 w-12" />
            </div>
          ))}
        </div>
        <Skeleton className="hidden md:block size-[34px] rounded-sm" />
      </div>
      <div className="flex justify-center gap-1.5 md:hidden">
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton
            key={index}
            className={cn("size-2 rounded-full", index === 0 && "w-[22px]")}
          />
        ))}
      </div>
    </div>
  );
}

// TODO: Replace with API data - currently using placeholder
const mockCategories: Category[] = [];

const CategorySection = () => {
  // Use ref to persist plugin instance and prevent re-creation on re-renders
  const autoplayRef = useRef(
    Autoplay({
      delay: 3000,
      stopOnInteraction: true,
      stopOnMouseEnter: true,
    })
  );

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <Typography
        variant="bodySMedium"
        className="md:text-base md:mb-9 text-center"
      >
        دستـه‌بنــدی‌هـای ویکـی‌جــا
      </Typography>

      <Carousel
        opts={{
          align: "start",
          loop: false,
          dragFree: true,
        }}
        plugins={[autoplayRef.current]}
        dir="rtl"
        className="w-full"
      >
        <div className="flex items-center gap-3">
          <CategoryNavigationPrev />

          <div className="flex-1 overflow-hidden pr-5 md:pr-0">
            <CarouselContent className="-mr-4">
              {mockCategories.length > 0 ? (
                mockCategories.map((category) => (
                  <CarouselItem
                    key={category.id}
                    className="basis-[19.5%] sm:basis-[18%] md:basis-[13%] lg:basis-[10.5%] xl:basis-[10%] pr-4"
                  >
                    <CategoryItem category={category} />
                  </CarouselItem>
                ))
              ) : (
                <CarouselItem className="basis-full pr-4">
                  <div className="flex items-center justify-center py-8">
                    <Typography
                      variant="captionRegular"
                      className="text-muted-foreground"
                    >
                      - (داده‌های دسته‌بندی در حال حاضر در دسترس نیست)
                    </Typography>
                  </div>
                </CarouselItem>
              )}
            </CarouselContent>
          </div>
          <CategoryNavigationNext />
        </div>

        {/* Custom Pagination - Only visible on mobile */}
        <CarouselDots className="justify-center flex md:hidden mt-6" />
      </Carousel>
    </div>
  );
};

export default CategorySection;
